import type {
  ItemType,
  MenuItemType,
  SubMenuType
} from "antd/es/menu/interface"
import React from "react"

import Icon from "../../Icon"
import { type PermissionPages } from "../SettingsPermissions/types/permissions.types.ts"

interface SubMenu extends Omit<SubMenuType, "children"> {
  children: Array<ItemType>
}

export type SidebarItem = (MenuItemType | SubMenu) & {
  key: PermissionPages | string
}

interface SidebarConfig {
  main: SidebarItem[]
  bottom: SidebarItem[]
}

export const REPORTS_NAV_KEY = "reports"
export const INVOICES_NAV_KEY = "invoices"
export const ARCHIVE_NAV_KEY = "archive"
export const MAP_NAV_KEY = "map"

// Marks the real nav row a Guide tour step spotlights (see the Product
// Tour's "nav-leads" step, tour/ProductTourContext.tsx) without changing how
// the row looks or behaves — `title` keeps the collapsed-sidebar tooltip
// that a plain string label gets for free, since wrapping `label` in a span
// loses it otherwise.
const navTarget = (id: string, text: string) => ({
  label: <span data-guide-target={id}>{text}</span>,
  title: text
})

export const sidebarConfig: SidebarConfig = {
  main: [
    {
      key: "home",
      icon: <Icon type={"Home"} />,
      ...navTarget("nav-home", "Home")
    },
    {
      key: REPORTS_NAV_KEY,
      icon: <Icon type={"Report"} />,
      ...navTarget("nav-reports", "Reports")
    },
    {
      key: "leads",
      icon: <Icon type={"Leads"} />,
      ...navTarget("nav-leads", "Leads")
    },
    {
      key: "projects",
      icon: <Icon type={"Projects"} />,
      ...navTarget("nav-projects", "Projects")
    },
    {
      key: ARCHIVE_NAV_KEY,
      icon: <Icon type={"Archive"} />,
      label: "Archive"
    },
    {
      key: "approval",
      icon: <Icon type={"Check"} />,
      label: "Approval",
      children: [
        {
          key: "proposals",
          label: "Proposal Review"
        },
        {
          key: "contracts",
          label: "Contract Review"
        },
        {
          key: "document_reviews",
          label: "Document Review"
        }
      ]
    },
    {
      key: INVOICES_NAV_KEY,
      icon: <Icon type={"Dollar"} />,
      ...navTarget("nav-invoices", "Invoices")
    },
    {
      key: "calendar",
      icon: <Icon type={"Calendar"} />,
      ...navTarget("nav-calendar", "Calendar")
    },
    {
      key: "files",
      icon: <Icon type={"Files"} />,
      ...navTarget("nav-files", "Files")
    },
    {
      key: MAP_NAV_KEY,
      icon: <Icon type={"Map"} />,
      label: "Location"
    },
    {
      key: "customers",
      icon: <Icon type={"Customers"} />,
      ...navTarget("nav-customers", "Customers")
    },
    {
      key: "subcontractors",
      icon: <Icon type={"Subcontractors"} />,
      ...navTarget("nav-subcontractors", "Subcontractors")
    },
    {
      key: "suppliers",
      icon: <Icon type={"Subcontractors"} />,
      label: "Suppliers"
    },
    {
      key: "team_management",
      icon: <Icon type={"Users"} />,
      ...navTarget("nav-team-management", "Team Management")
    }
  ],
  bottom: [
    {
      key: "guide",
      icon: <Icon type={"Guide"} />,
      label: "Guide"
    },
    {
      key: "settings",
      icon: <Icon type={"Settings"} />,
      ...navTarget("nav-settings", "Settings")
    }
  ]
}
